import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import SectionHeader from "../components/site/SectionHeader";
import SafeImage from "../components/site/SafeImage";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph } from "../lib/schema";

const CATEGORY_INTROS = {
  "Eye Anatomy": {
    tagline: "How the human eye actually works.",
    body: "Ophthalmology begins with anatomy. These illustrated guides walk you through the cornea, lens, retina, optic nerve and the vitreous — the six structures your ophthalmologist examines at every visit. Whether you are preparing for cataract surgery, understanding a retina report, or simply curious about how vision forms in the brain, this section gives you the vocabulary to have an informed conversation with any eye specialist. Each explainer is reviewed by Dr. Shantanu Kumar Gupta (MS, MAMC — New Delhi) and pairs medical accuracy with plain-language patient examples from our Varanasi clinic.",
  },
  "Eye Care Tips": {
    tagline: "Daily habits that protect your vision.",
    body: "The single biggest predictor of long-term vision is not surgery — it is the small choices you make every day. Screen breaks, hydration, UV protection, sleep quality, blood-sugar control and posture during reading each contribute to how your eyes age. This category collects the everyday-eye-care advice we most frequently give patients at Shantanu Netralaya — from monsoon-season conjunctivitis prevention in Varanasi to the 20-20-20 rule for children studying online. Each tip is grounded in current ophthalmology guidelines and adapted for the eastern-UP climate and lifestyle.",
  },
  "Surgical Recovery Guides": {
    tagline: "What to expect after your eye surgery.",
    body: "Modern ophthalmic surgery — cataract removal, DMEK/DSAEK corneal grafts, retinal laser, anti-VEGF injections — is fast and mostly painless, but the first two weeks of recovery are where outcomes are made or lost. This section walks you through the recovery timeline day-by-day: which drops to instill and when, how to sleep, when driving and reading resume, warning signs that need an urgent call, and the follow-up schedule you should insist on. Written and reviewed by Dr. Shantanu Kumar Gupta, based on protocols followed at Guru Nanak Eye Centre and Shantanu Netralaya Eye Hospital, Varanasi.",
  },
  "IOL Comparisons": {
    tagline: "Choosing the right intraocular lens.",
    body: "The lens implanted during cataract surgery stays with you for life. Monofocal, toric, extended-depth-of-focus (EDOF) and trifocal IOLs each solve different problems — and cost very differently. These comparison guides break down the visual outcome, night-vision trade-offs, spectacle-independence and price ranges so that you can walk into your cataract consultation already knowing which questions to ask. All comparisons are neutral, brand-agnostic and updated as newer premium IOL platforms reach India.",
  },
};

const ContentHub = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category") || "All";
  const [query, setQuery] = useState(params.get("q") || "");

  const catIntro = activeCategory !== "All" ? CATEGORY_INTROS[activeCategory] : null;

  usePageMeta({
    title: activeCategory === "All"
      ? "Content Hub — Eye Health Library | Dr. Shantanu Gupta"
      : `${activeCategory} — Eye Health Library | Dr. Shantanu Gupta`,
    path: activeCategory === "All" ? "/content-hub" : `/content-hub?category=${activeCategory}`,
    description: catIntro
      ? `${catIntro.tagline} ${catIntro.body.slice(0, 130)}…`
      : "Patient education library on eye diseases, anatomy, technology and lens comparisons — reviewed by Dr. Shantanu Kumar Gupta, MS (MAMC).",
  });
  usePageJsonLd(graph([breadcrumb([{ name: "Home", path: "/" }, { name: "Content Hub", path: "/content-hub" }])]));

  useEffect(() => {
    api.get("/hub").then((r) => setItems(r.data));
    api.get("/hub/categories").then((r) => setCategories(["All", ...r.data]));
  }, []);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const catOk = activeCategory === "All" || it.category === activeCategory;
      const q = query.trim().toLowerCase();
      const qOk = !q || it.title.toLowerCase().includes(q) || it.excerpt.toLowerCase().includes(q);
      return catOk && qOk;
    });
  }, [items, activeCategory, query]);

  const setCategory = (cat) => {
    const p = new URLSearchParams(params);
    if (cat === "All") p.delete("category");
    else p.set("category", cat);
    setParams(p);
  };

  return (
    <div data-testid="content-hub-page">
      <section className="pt-20 pb-10 container-page">
        <div className="overline mb-4">Content Hub{activeCategory !== "All" && ` · ${activeCategory}`}</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          {catIntro ? catIntro.tagline : "The Shantanu Netralaya Eye Knowledge Center."}
        </h1>
        <p className="mt-5 max-w-2xl prose-elegant">
          {catIntro ? catIntro.body : "Evidence-based, patient-friendly explainers on eye diseases, technology, IOL choices, surgical recovery and everyday eye-care habits — reviewed by Dr. Shantanu Kumar Gupta."}
        </p>
        {catIntro && (
          <div className="mt-6">
            <button
              onClick={() => setCategory("All")}
              className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))] hover:underline"
              data-testid="hub-back-to-all"
            >
              ← Browse all categories
            </button>
          </div>
        )}
      </section>

      <section className="py-8 border-y border-border bg-[hsl(var(--secondary))]/40">
        <div className="container-page flex flex-col md:flex-row md:items-center gap-6">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles, diseases, procedures…"
              className="pl-9"
              data-testid="hub-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`hub-cat-${c.replace(/\s/g, "-").toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.16em] border transition ${
                  activeCategory === c
                    ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                    : "border-border text-muted-foreground hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 container-page grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="hub-list">
        {filtered.map((it) => (
          <Link key={it.slug} to={`/content-hub/${it.slug}`} className="group block" data-testid={`hub-card-${it.slug}`}>
            <div className="relative overflow-hidden rounded-xl aspect-[4/3] border border-border">
              <SafeImage src={it.cover_image} alt={it.title} label={it.title} className="w-full h-full" />
              <div className="absolute top-3 left-3 bg-white/90 dark:bg-black/70 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] rounded z-10">
                {it.category}
              </div>
            </div>
            <div className="mt-5">
              <h3 className="font-serif text-2xl leading-snug group-hover:text-[hsl(var(--primary))] transition-colors">{it.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{it.excerpt}</p>
              <div className="mt-4 text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))]">{it.reading_time} min read</div>
            </div>
          </Link>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-muted-foreground">
            No articles match your filter.
          </div>
        )}
      </section>
    </div>
  );
};

export default ContentHub;
